import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useUploadCustomEmojiMutation } from 'store/services/emoji/emojiApi';
import './CustomEmojiUploadModal.css';

interface CustomEmojiUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  remainingUploads: number;
  initialFile?: File;
}

const MAX_FILE_SIZE = 256 * 1024; // 256KB
const ACCEPTED_TYPES = ['image/png', 'image/gif'];
const NAME_REGEX = /^[a-zA-Z0-9_]+$/;

export const CustomEmojiUploadModal: React.FC<CustomEmojiUploadModalProps> = ({
  isOpen,
  onClose,
  remainingUploads: initialRemaining,
  initialFile,
}) => {
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [remaining, setRemaining] = useState(initialRemaining);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadCustomEmoji, { isLoading: isUploading }] = useUploadCustomEmojiMutation();

  useEffect(() => {
    setRemaining(initialRemaining);
  }, [initialRemaining]);

  useEffect(() => {
    if (isOpen && initialFile) {
      handleFileChange(initialFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialFile]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateFile = useCallback((f: File): Promise<string[]> => {
    return new Promise((resolve) => {
      const errors: string[] = [];

      if (!ACCEPTED_TYPES.includes(f.type)) {
        errors.push('File must be PNG or GIF');
      }

      if (f.size > MAX_FILE_SIZE) {
        errors.push(`File size must be under 256KB (current: ${Math.round(f.size / 1024)}KB)`);
      }

      // Check dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width !== img.height) {
          errors.push(`Image must be square (current: ${img.width}x${img.height})`);
        }
        if (img.width < 32 || img.width > 128) {
          errors.push(`Dimensions must be 32-128px (current: ${img.width}px)`);
        }
        resolve(errors);
      };
      img.onerror = () => {
        errors.push('Could not read image file');
        resolve(errors);
      };
      img.src = URL.createObjectURL(f);
    });
  }, []);

  const handleFileChange = useCallback(async (f: File) => {
    const errors = await validateFile(f);
    setValidationErrors(errors);

    if (previewUrl) URL.revokeObjectURL(previewUrl);

    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }, [validateFile, previewUrl]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFileChange(f);
    e.target.value = '';
  }, [handleFileChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileChange(f);
  }, [handleFileChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const slug = name.toLowerCase().replace(/\s+/g, '_');

  const nameErrors: string[] = [];
  if (name.length > 0 && name.length < 2) nameErrors.push('Name must be at least 2 characters');
  if (name.length > 30) nameErrors.push('Name must be 30 characters or fewer');
  if (name.length > 0 && !NAME_REGEX.test(name)) nameErrors.push('Only letters, numbers, and underscores');

  const canUpload = file && name.length >= 2 && name.length <= 30 && NAME_REGEX.test(name) &&
    validationErrors.length === 0 && remaining > 0 && !isUploading;

  const handleUpload = async () => {
    if (!canUpload || !file) return;

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', name);

      const result = await uploadCustomEmoji(formData).unwrap();
      setRemaining(result.remaining);
      toast.success(`Your emoji :${result.slug}: is now available to the community!`);
      onClose();
    } catch (error: any) {
      if (error.status === 403) {
        toast.error('Elite subscription required to upload custom emoji');
      } else if (error.status === 409) {
        toast.error('An emoji with that name already exists. Try a different name.');
      } else if (error.status === 429) {
        toast.error('Upload limit reached');
      } else {
        toast.error(error.data?.message || 'Failed to upload emoji');
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="emoji-upload-overlay" onClick={onClose}>
      <div className="emoji-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="emoji-upload-header">
          <h3>Upload Custom Emoji</h3>
          <button className="emoji-upload-close" onClick={onClose} type="button">&times;</button>
        </div>

        <div
          className={`emoji-upload-dropzone${isDragging ? ' emoji-upload-dropzone--active' : ''}${previewUrl ? ' emoji-upload-dropzone--has-file' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" className="emoji-upload-preview-img" />
          ) : (
            <div className="emoji-upload-dropzone-text">
              <span className="emoji-upload-dropzone-icon">+</span>
              <span>Drop image here or click to upload</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.gif"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
        </div>

        {validationErrors.length > 0 && (
          <div className="emoji-upload-errors">
            {validationErrors.map((err, i) => <div key={i}>{err}</div>)}
          </div>
        )}

        <div className="emoji-upload-field">
          <label className="emoji-upload-label">Emoji Name</label>
          <input
            type="text"
            className="emoji-upload-input"
            placeholder="e.g. cool_lotus"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={30}
          />
          {name.length > 0 && nameErrors.length === 0 && (
            <div className="emoji-upload-slug-preview">:{slug}:</div>
          )}
          {nameErrors.length > 0 && (
            <div className="emoji-upload-errors">
              {nameErrors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
        </div>

        <div className="emoji-upload-specs">
          <div className="emoji-upload-specs-title">Requirements</div>
          <ul>
            <li>Format: PNG or GIF (animated GIF supported)</li>
            <li>Max size: 256KB</li>
            <li>Dimensions: Square, 32-128px (resized to 64x64)</li>
          </ul>
        </div>

        <div className="emoji-upload-guidelines">
          <div className="emoji-upload-specs-title">Guidelines</div>
          <ul>
            <li>No profanity, NSFW content, or hateful imagery</li>
            <li>Emoji that violate community guidelines will be removed</li>
          </ul>
        </div>

        <div className="emoji-upload-remaining">
          {remaining} of 10 uploads remaining
        </div>

        {previewUrl && name.length >= 2 && nameErrors.length === 0 && (
          <div className="emoji-upload-live-preview">
            <span className="emoji-upload-live-preview-label">Preview:</span>
            <span className="emoji-upload-live-preview-pill">
              <img src={previewUrl} alt="Preview" className="emoji-upload-live-preview-img" />
              <span>1</span>
            </span>
          </div>
        )}

        <div className="emoji-upload-actions">
          <button className="emoji-upload-cancel" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="emoji-upload-submit"
            onClick={handleUpload}
            disabled={!canUpload}
            type="button"
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
